import { createRef, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import TextField from '@mui/material/TextField';

import InfoBox from './box/InfoBox';
import NotesBox from './box/NotesBox';
import CustomBox from './box/CustomBox';
import EditableLabel from './editable_label/index';
import { selectTrace } from '../../../../features/case.slice';
import { selectMicroservices } from '../../../../features/microservices.slice';
import { isEmptyObject } from '../../../../utils/utils';
import { types, ASIDE_NODE, ASIDE_EDGE, ASIDE_FETCHERS } from '../../../../utils/settings';
import spinnerImg from '../../../../assets/spinner-solid-ube-20.png';
import imgCalendarGreen from '../../../../assets/markers/calendar_green.png';
import imgCalendarRed from '../../../../assets/markers/calendar_red.png';

import styles from '../../../../styles/case/aside/Aside.module.scss';

const rotate = keyframes`
  0%   {transform: rotate(0deg);}
  25%  {transform: rotate(90deg);}
  50%  {transform: rotate(180deg);}
  75%  {transform: rotate(270deg);}
  100% {transform: rotate(360deg);}
`;

const mixin = css`
  animation: ${rotate} 3s linear infinite;
`;

const Circle = styled.div`
  &&& {
    display: inline-block;
    width: 20px;
    height: 20px;

    ${props => (props.run === 2) ? `
      background-image: url(${spinnerImg});
    ` : `
      border-radius: 20px;
    `}
    
    ${props => (props.run === -1) && `
      background-color: #E16150;
    `}
    ${props => (props.run === 0) && `
      background-color: #f3f5f6;
    `}
    ${props => (props.run === 1) && `
      background-color: #49C4A3;
    `}
    ${props => (props.run === 2) && mixin}
  }
`;

// 20px by 20px clock drawn with css
const Clock = styled.div`
  &&& {
    display: inline-block;
    width: 20px;
    height: 20px;
    
    ${props => (props.warning) ? `
      background-image: url(${imgCalendarRed});
    ` : `
      background-image: url(${imgCalendarGreen});
    `}
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }
`;



const Wrapper = styled.div`
  &&& {
    right: ${props => props.show ? '10px' : 'min(-20vw, -400px)'};
    transition: right 0.5s;
  }
`;

const Button = styled.button`
  &&& {
    ${props => props.selected ? `
      border: none;
      background-color: #8e8f9c;
      
      p {
        background-color: #8e8f9c;
        color: #282840;
      }
    ` : `
      border: 2px solid #8e8f9c;
      background-color: transparent;
    `}
  }
`;

const Label = styled.p`
  &&& {
    ${props => !props.label && `
      color: rgba(243, 246, 247, 0.5);
    `}
  }
`;

const ts2str = (ts) => (new Date(ts).toISOString().replace('T', ' ').slice(0, -5) + ' GMT');

function Aside(props) {
  const ref = useRef(null);
  const refGroupName = useRef(null);

  const [crtTab, setCrtTab] = useState('');
  const [nodeFetcherTabActive, setNodeFetcherTabActive] = useState(false);
  const [customNote, setCustomNote] = useState(null);

  const microservices = useSelector(selectMicroservices);
  let nodeType = props.content?.node?.type;
  if (props.content?.nodes?.length > 0) {
    let nodesHaveSameType = true;
    for (let i = 1; i < props.content.nodes.length; i++) {
      if (props.content.nodes[i].type !== props.content.nodes[0].type) {
        nodesHaveSameType = false;
        break;
      }
    }
    if (nodesHaveSameType)
      nodeType = props.content.nodes[0].type;
  }
  const filteredMicroservices = nodeType && microservices ?
    Object.keys(microservices).filter(id =>
      microservices[id].fetchers.find(fetcher =>
        fetcher.status !== -1 &&
        Object.keys(fetcher.types).find(type => type === nodeType || type === '*')
      )
    ).sort((a, b) => microservices[a].name.localeCompare(microservices[b].name)) :
    [];

  function deleteEdge(event) {
    let msg = 'Are you sure you want to delete the edge '
      + `from '${props.content.edge.from_value}' (${props.content.edge.from_type}) `
      + `to '${props.content.edge.to_value}' (${props.content.edge.to_type})?`
    if (!window.confirm(msg)) return;
    props.deleteObject && props.deleteObject(props.content);
  }

  function deleteNode(event) {
    let msg = `Are you sure you want to delete the node '${props.content.node?.value}' (${props.content.node?.type})?`
    if (!window.confirm(msg)) return;
    props.deleteObject && props.deleteObject(props.content);
  }

  function deleteNodes(event) {
    let msg = `Are you sure you want to delete the selected '${props.content.mnodes?.length}' nodes?`
    if (!window.confirm(msg)) return;
    props.deleteObject && props.deleteObject(props.content.nodes);
  }

  function addNote(event) {
    if (crtTab === ASIDE_FETCHERS) setCrtTab(ASIDE_NODE);
    if (props.content.node && props.onAddNote) props.onAddNote(props.content.node);

    // setCustomNote((
    //   <CustomBox
    //     type={props.content.type}
    //     content={props.content[props.content.type]}
    //     setCustomNote={props.setCustomNote}
    //   />
    // ));
  }

  const edgeJsx = props.content.type === ASIDE_EDGE && props.content.edge && (
    <>
      <div>
        <header>
          <div className={styles.buttons}>
            <Button selected={crtTab === ASIDE_EDGE}>
              <p>EDGE INFO</p>
            </Button>
          </div>

          <div className={styles.info}>
            <p>{props.content.edge.arrows === '' ? 'NODE 1' : 'SOURCE NODE'}</p>
            <p title={props.content.edge.from_type}>{props.content.edge.from_value}</p>
          </div>
          <div className={styles.info}>
            <p>{props.content.edge.arrows === '' ? 'NODE 2' : 'DESTINATION NODE'}</p>
            <p title={props.content.edge.to_type}>{props.content.edge.to_value}</p>
          </div>
        </header>
        <main>
          {customNote !== null && customNote}
          {(props.content?.edge?.info?.length) ?
            props.content.edge.info.map((info, index) =>
              <InfoBox key={index} label={info.label} content={info.content} custom={info.custom} />
            ) :
            <div className={styles.noInfo}>
              {/*<FontAwesomeIcon icon={faFrown}/>*/}
              <p>no info to show</p>
            </div>
          }
        </main>
      </div>
      <footer>
        <div className={styles.buttons}>
          <button onClick={deleteEdge}>
            <p>delete edge</p>
          </button>
        </div>
      </footer>
    </>
  );

  const mnodesJsx = props.content.type === 'multiple' && props.content.nodes && (
    <>
      <div>
        <header>
          <div className={styles.buttons}>
            <Button selected={crtTab === 'multiple'}>
              <p>SELECTION INFO</p>
            </Button>
          </div>

          <div className={styles.info}>
            <p>SELECTED NODES</p>
            <p>{props.content.nodes.length} nodes</p>
          </div>
        </header>
        <main>
          <hr style={{margin: 10}} />
          <fieldset style={{padding: 10, margin: 5}}>
            <legend>Node grouping</legend>
            <div className={styles.groupingDescription}>This will create a new node that is connected to selected nodes. To ungroup nodes, select the group node in the graph and delete it.</div>
            <TextField
              inputRef={refGroupName}
              className={styles.textField}
              variant="standard"
              InputLabelProps={{ shrink: true }}
              placeholder="Name"
              onKeyDown={(e) => {if (e.key === 'Enter') props.onGroup(refGroupName.current.value)}}
            />
            <button className={styles.groupButton} onClick={() => {props.onGroup(refGroupName.current.value)}}>Group</button>
          </fieldset>
          <hr style={{margin: 10}} />
          {
            nodeType ? filteredMicroservices.map((id, index) => (
              <table key={index} cellSpacing='0'>
                <thead>
                  <tr>
                    <th colSpan='3'>{microservices[id].name}</th>
                  </tr>
                </thead>
                <tbody>
                  {microservices[id].fetchers
                    .filter(fetcher => Object.keys(fetcher.types).find(type => type === nodeType || type === '*'))
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((fetcher, index) => (
                      <tr key={index} title='Run fetcher on selected nodes'
                        onClick={() => {
                          props.content.nodes.forEach(node => props.sendRequest(node, id, fetcher.name));
                        }}
                      >
                        <td><Circle run={(() => {
                          let commonRun = props.content.nodes[0]?.trace?.[id]?.[fetcher.name]?.run || 0;
                          for (let i = 1; i < props.content.nodes.length; i++) {
                            if (props.content.nodes[i]?.trace?.[id]?.[fetcher.name]?.run !== commonRun) {
                              commonRun = 0;
                              break;
                            }
                          }
                          return commonRun;
                        })()} /></td>
                        <td>{fetcher.name}</td>
                        <td></td>
                      </tr>
                      ))}
                </tbody>
              </table>
            ))
            : <></>
          }
        </main>
      </div>
    </>
  );

  /*
  function onDeleteInfoBox(index) {
    return (event) => {
      event.preventDefault();
      if (!window.confirm('Are you sure you want to delete this note?')) return;

      let newInfo;
      if (index === 0) {
        props.content.info.shift();
        newInfo = props.content.info;
      } else if (index === props.content.info.length - 1) {
        props.content.info.pop();
        newInfo = props.content.info;
      } else {
        newInfo = props.content.info.slice(0, index).concat(props.content.info.slice(index));
      }

      props.setContent(content => ({ ...content, info: newInfo }));
      if (props.type === ASIDE_EDGE) {
        props.edges.update({
          id: props.content.id,
          info: newInfo
        });
      } else if (props.type === ASIDE_NODE) {
        props.nodes.update({
          id: props.content.id,
          info: newInfo
        });
      }
    };
  }
  */

  const nodeMainJsx = (
    <>
      {props.content?.node?.notes &&
        <NotesBox notes={props.content.node.notes} onEdit={props.onEditNote} onDelete={props.onDeleteNote} />
      }
      {(props.content?.node?.info?.length) ?
        props.content.node.info.map((info, index) =>
          <InfoBox key={'info_' + index} label={info.label} content={info.content} custom={info.custom} onDelete={(index) => console.log("TODO: delete info ", index)} />
        ) :
        <div className={styles.noInfo}>
          {/*<FontAwesomeIcon icon={faFrown}/>*/}
          <p>no info to show</p>
        </div>
      }
    </>
  );

  const nodeTrace = props.content.node?.trace;

  const fetchersMainJsx = (
    <>
      {isEmptyObject(microservices) ?
        <div className={styles.noInfo}>
          {/*<FontAwesomeIcon icon={faFrown}/>*/}
          <p>no microservice available</p>
        </div> :
        filteredMicroservices.map((id, index) => (
          <table key={index} cellSpacing='0'>
            <thead>
              <tr>
                <th colSpan='3'>{microservices[id].name}</th>
              </tr>
            </thead>
            <tbody>
              {nodeType && microservices[id].fetchers
                .filter(fetcher => Object.keys(fetcher.types).find(type => type === nodeType || type === '*'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fetcher, index) => (
                  <tr key={index} title={
                    (nodeTrace?.[id]?.[fetcher.name]?.ts > 0 ? 'Last run: ' + new Date(nodeTrace[id][fetcher.name].ts).toLocaleString() + ' (local time)': 'Not executed yet') + '\n' +
                    (nodeTrace?.[id]?.[fetcher.name]?.info || '')
                  }
                  onClick={() => props.sendRequest(props.content.node, id, fetcher.name)}
                  >
                    <td>
                      <Circle
                        run={nodeTrace?.[id]?.[fetcher.name]?.run || 0}
                      />
                    </td>
                    <td>{fetcher.name}</td>
                    <td>
                      { nodeTrace?.[id]?.[fetcher.name]?.timeInterval.from && nodeTrace?.[id]?.[fetcher.name]?.timeInterval.to ?
                        <Clock
                          warning={
                            nodeTrace[id][fetcher.name].timeInterval.from > props.timeFilter.from ||
                            nodeTrace[id][fetcher.name].timeInterval.to < props.timeFilter.to
                          }
                          title={'Executed with time filter:\nFrom: ' + ts2str(nodeTrace[id][fetcher.name].timeInterval.from) + '\nTo: ' + ts2str(nodeTrace[id][fetcher.name].timeInterval.to)}
                          onClick={(e) => {console.log('!! ', nodeTrace?.[id]?.[fetcher.name])}}
                        />
                        : <></>
                      }
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ))
      }
    </>
  );

  /*
  useEffect(() => {
    function handleClickOutside(event) {
      if (!ref.current || ref.current.contains(event.target) || !edit) return;
      const value = inputRef.current.value;
      if (props.content.label === value || value.trim() === '') {
        setEdit(false);
        return;
      }
      if (window.confirm('The changes made to the edge label are not saved. Do you want to save them?')) {
        const id = props.content.id;
        props.edges.update({
          id: id,
          label: value
        });
        props.setContent(props.edges.get(id));
      }

      setEdit(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [edit, inputRef, props, props.content?.label, ref]);
  */

  useEffect(() => {
    //console.log("ASIDE CONTENT: ", props.content, crtTab);
    if (props.content?.type === 'node' && crtTab !== ASIDE_FETCHERS) setCrtTab(ASIDE_NODE);
    if (props.content?.type === 'edge') setCrtTab(ASIDE_EDGE);

  }, [props.content, crtTab])

  if (!props.show) {
    //console.log("HIDDEN ASIDE");
    return <></>
  }

  return props.content ? (
    <Wrapper className={styles.aside} ref={ref} show={props.show}>
      {props.content.type === ASIDE_EDGE ? edgeJsx : props.content.type === 'multiple' ? mnodesJsx :
        <>
          <div>
            <header>
              <div className={styles.buttons}>
                <Button selected={crtTab === ASIDE_NODE && !nodeFetcherTabActive} onClick={() => {setCrtTab(ASIDE_NODE); setNodeFetcherTabActive(false); }}>
                  <p>NODE INFO</p>
                </Button>
                <Button selected={crtTab === ASIDE_FETCHERS || nodeFetcherTabActive} onClick={() => {setCrtTab(ASIDE_FETCHERS); setNodeFetcherTabActive(true);}}>
                  <p>FETCHERS</p>
                </Button>
              </div>

              <div className={styles.info}>
                <p>TYPE</p>
                <p>{props.content.node.type}</p>
              </div>
              <div className={styles.info}>
                <p>VALUE</p>
                <p>{props.content.node.value}</p>
              </div>
              <div className={styles.info}>
                <p>LABEL</p>
                <EditableLabel
                  default={props.content.node.value}
                  value={props.content.node.visjsProps?.label}
                  onSave={label => props.setNodeLabel(props.content.node, label)}
                />
              </div>
            </header>

            <main>
              {crtTab === ASIDE_NODE  && !nodeFetcherTabActive ? nodeMainJsx : fetchersMainJsx}
            </main>
          </div>

          <footer>
            <div className={styles.buttons}>
              <button onClick={addNote}>
                <p>add note</p>
              </button>
              <button onClick={deleteNode}>
                <p>delete node</p>
              </button>
            </div>
          </footer>
        </>
      }
    </Wrapper>
  ) : <></>;
}

export default Aside;